import { ButtonHTMLAttributes, FC } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { Icon } from '../icon/Icon';

export type BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

const BaseButton: FC<Omit<BaseButtonProps, 'iconLeft' | 'iconRight'>> = styled.button`
  ${tw`inline-flex items-center justify-center flex-nowrap outline-none px-3 py-half rounded-sm whitespace-nowrap select-none transition-all duration-fast ease-in-out`}
  &[disabled] {
    ${tw`cursor-default`}
  }
`;

export interface IconButtonProps extends BaseButtonProps {
  active?: boolean;
  disabled?: boolean;
  iconLeft?: string;
  iconRight?: string;
  loading?: boolean;
}

const IconButton: FC<IconButtonProps> = ({ loading, disabled, iconLeft, iconRight, children, ...props }) => (
  <BaseButton disabled={loading || disabled} {...props}>
    {loading ? (
      <Icon aria-label="loading" name="circle-loader" tw="animate-spin transition-all duration-fast leading-normal" />
    ) : (
      <>
        {iconLeft && <Icon name={iconLeft} tw="mr-1 leading-none text-xl text-current" />}
        {children}
        {iconRight && <Icon name={iconRight} tw="ml-1 leading-none text-xl text-current" />}
      </>
    )}
  </BaseButton>
);

export const FillButton: FC<IconButtonProps> = styled(IconButton)`
  ${tw`bg-primary hover:bg-primary-dark focus:bg-indigo active:bg-primary-dark text-white font-medium`}
  ${({ active }) => active && tw`bg-primary hover:bg-indigo active:bg-indigo`}
  &[disabled] {
    ${({ loading }) => (loading ? tw`bg-primary text-white` : tw`bg-gray-50 text-gray-15`)}
  }
`;

export const GhostButton: FC<IconButtonProps> = styled(IconButton)`
  box-shadow: 0 0 0 2px currentColor inset;
  ${tw`bg-transparent font-medium text-primary-medium focus:text-primary-dark active:text-primary-dark`}
  ${({ active }) => active && tw`text-primary-dark active:text-primary-dark`}
  &[disabled] {
    ${({ loading }) => (loading ? tw`bg-transparent text-primary-light` : tw`text-gray-15`)}
  }
`;

export const TextButton: FC<IconButtonProps> = styled(IconButton)`
  ${tw`px-0 bg-transparent text-gray-70 hover:text-primary focus:text-primary active:text-primary-dark`}
  ${({ active }) => active && tw`text-primary hover:text-primary active:text-primary-dark`}
  &[disabled] {
    ${({ loading }) => (loading ? tw`bg-transparent text-gray-50` : tw`text-gray-15`)}
  }
`;
